import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    BellRing,
    CalendarClock,
    CircleCheck,
    CirclePlus,
    MessageCircle,
    PencilLine,
    RotateCcw
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { formatDate, formatDateTime } from '@/lib/dates'
import { getFollowUpBucket } from '@/lib/clinic/selectors'
import { FollowUpBadge, MessageStatusBadge } from '@/components/app/status-badge'
import EmptyState from '@/components/app/empty-state'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { MESSAGE_KIND_LABELS } from '@/constants/clinic'

interface FollowUpHistoryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    followUpId: string
}

const EVENT_ICONS: Record<string, { icon: LucideIcon, className: string }> = {
    created: { icon: CirclePlus, className: 'bg-sky-50 text-sky-700' },
    rescheduled: { icon: CalendarClock, className: 'bg-amber-50 text-amber-700' },
    reminder: { icon: BellRing, className: 'bg-emerald-50 text-emerald-700' },
    completed: { icon: CircleCheck, className: 'bg-emerald-50 text-emerald-700' },
    reopened: { icon: RotateCcw, className: 'bg-slate-100 text-slate-700' },
    updated: { icon: PencilLine, className: 'bg-slate-100 text-slate-700' },
    message: { icon: MessageCircle, className: 'bg-teal-50 text-teal-700' }
}

const EVENT_TITLES: Record<string, string> = {
    created: 'Follow-up created',
    rescheduled: 'Rescheduled',
    reminder: 'Reminder sent',
    completed: 'Completed',
    reopened: 'Reopened',
    updated: 'Details updated'
}

const FollowUpHistoryDialog = ({ open, onOpenChange, followUpId }: FollowUpHistoryDialogProps): ReactNode => {
    const { data, today } = useClinic()
    const followUp = data.followUps.find(item => item.id === followUpId)
    const patient = followUp ? data.patients.find(item => item.id === followUp.patientId) : undefined
    const messages = data.messages.filter(item => item.followUpId === followUpId)

    const entries = followUp
        ? [
            ...followUp.history.map(event => ({ id: event.id, at: event.at, type: event.type, title: EVENT_TITLES[event.type] ?? 'Update', note: event.note, message: null as null | typeof messages[number] })),
            ...messages.map(message => ({ id: message.id, at: message.sentAt || message.createdAt, type: 'message', title: MESSAGE_KIND_LABELS[message.kind], note: message.body, message }))
        ].sort((a, b) => b.at.localeCompare(a.at))
        : []

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg'>
                <DialogHeader>
                    <DialogTitle>Reminder history</DialogTitle>
                    <DialogDescription>
                        {followUp && patient
                            ? `${patient.name}, due ${formatDate(followUp.dueDate)}. ${followUp.reminderCount} reminder${followUp.reminderCount === 1 ? '' : 's'} sent.`
                            : 'This follow-up is no longer available.'}
                    </DialogDescription>
                </DialogHeader>

                {followUp && (
                    <div className='flex flex-wrap items-center gap-2'>
                        <FollowUpBadge bucket={getFollowUpBucket(followUp, today)} />
                        {followUp.treatment && <span className='text-xs text-muted-foreground'>{followUp.treatment}</span>}
                    </div>
                )}

                {entries.length === 0
                    ? <EmptyState icon={BellRing} title='No activity yet' description='Reminders and changes will appear here.' compact />
                    : (
                        <ol className='relative grid gap-4 border-l pl-6'>
                            {entries.map(entry => {
                                const meta = EVENT_ICONS[entry.type] ?? EVENT_ICONS.updated
                                const Icon = meta.icon
                                return (
                                    <li key={entry.id} className='relative'>
                                        <span className={cn('absolute top-0 -left-[2.3rem] flex size-7 items-center justify-center rounded-full ring-4 ring-popover', meta.className)}>
                                            <Icon className='size-3.5' aria-hidden='true' />
                                        </span>
                                        <div className='flex flex-wrap items-center gap-2'>
                                            <p className='text-sm font-medium'>{entry.title}</p>
                                            {entry.message && <MessageStatusBadge status={entry.message.status} />}
                                        </div>
                                        <p className='text-xs text-muted-foreground'>{formatDateTime(entry.at)}</p>
                                        {entry.note && (
                                            <p className={cn('mt-1 text-sm text-foreground/80', entry.message && 'rounded-lg bg-muted/60 p-2 text-xs whitespace-pre-wrap')}>
                                                {entry.note}
                                            </p>
                                        )}
                                    </li>
                                )
                            })}
                        </ol>
                    )}
            </DialogContent>
        </Dialog>
    )
}

export default FollowUpHistoryDialog
